/**
 * UranIA PA — Admin Console hub (``/admin``).
 *
 * Pagina di landing che il ``DashboardAdminConsoleWidget`` (US-011)
 * apre al click. Aggrega le 5 sezioni della console:
 *
 *   1. Tenant (US-008) — funzionante
 *   2. Skill (US-019 + US-029) — funzionante
 *   3. Cutover gates (US-F5-08) — funzionante
 *   4. Corpus & Ingestion — roadmap Fase 4 (deferred)
 *   5. Tipi documento (template DOCX) — roadmap Fase 4 (deferred)
 *
 * RBAC guard UX: solo ruoli admin (platform_admin / tenant_admin)
 * vedono le card. La rotta resta accessibile anche da non-admin
 * mostrando il fallback ``forbidden`` (l'autorizzazione reale e'
 * server-side).
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../lib/auth/auth-store.ts';
import { principalIsAnyAdmin } from '../../lib/auth/rbac.ts';
import { Route as AmministrazioneSegmentRoute } from '../_amministrazione.tsx';

interface ConsoleCardConfig {
  readonly key: string;
  readonly to: string | null;
  readonly icon: JSX.Element;
  readonly accent: string;
  readonly status: 'available' | 'roadmap' | 'roadmap_clickable';
}

function IconBuilding(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-2.5v-2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5V15h-2.5a.5.5 0 0 1-.5-.5v-12z" />
      <path d="M3 14.5V14a.5.5 0 0 1 .5-.5H4V2.5A1.5 1.5 0 0 1 5.5 1h5A1.5 1.5 0 0 1 12 2.5V13.5h.5a.5.5 0 0 1 .5.5v.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5z" />
      <path d="M5.5 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H9a.5.5 0 0 1-.5-.5zm-3 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H9a.5.5 0 0 1-.5-.5zM5.5 8a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H9a.5.5 0 0 1-.5-.5zm-3 2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H9a.5.5 0 0 1-.5-.5z" />
    </svg>
  );
}

function IconCpu(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5 0a.5.5 0 0 1 .5.5V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2A2.5 2.5 0 0 1 14 4.5h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14a2.5 2.5 0 0 1-2.5 2.5v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14A2.5 2.5 0 0 1 2 11.5H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2A2.5 2.5 0 0 1 4.5 2V.5A.5.5 0 0 1 5 0zm-.5 3A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13h7a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 11.5 3h-7zM5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3zM6.5 6a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
    </svg>
  );
}

function IconShield(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.338 1.59a61 61 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56z" />
      <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
    </svg>
  );
}

function IconBook(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z" />
    </svg>
  );
}

function IconFileTextLarge(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zM4.5 7a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 9a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z" />
      <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z" />
    </svg>
  );
}

function IconDatabase(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M4.318 2.687C5.234 2.271 6.536 2 8 2s2.766.27 3.682.687C12.644 3.125 13 3.627 13 4c0 .374-.356.875-1.318 1.313C10.766 5.729 9.464 6 8 6s-2.766-.27-3.682-.687C3.356 4.875 3 4.373 3 4c0-.374.356-.875 1.318-1.313ZM13 5.698V7c0 .374-.356.875-1.318 1.313C10.766 8.729 9.464 9 8 9s-2.766-.27-3.682-.687C3.356 7.875 3 7.373 3 7V5.698c.271.202.58.378.904.525C4.978 6.711 6.427 7 8 7s3.022-.289 4.096-.777A4.92 4.92 0 0 0 13 5.698ZM14 4c0-1.007-.875-1.755-1.904-2.223C11.022 1.289 9.573 1 8 1s-3.022.289-4.096.777C2.875 2.245 2 2.993 2 4v9c0 1.007.875 1.755 1.904 2.223C4.978 15.71 6.427 16 8 16s3.022-.289 4.096-.777C13.125 14.755 14 14.007 14 13V4Zm-1 4.698V10c0 .374-.356.875-1.318 1.313C10.766 11.729 9.464 12 8 12s-2.766-.27-3.682-.687C3.356 10.875 3 10.373 3 10V8.698c.271.202.58.378.904.525C4.978 9.71 6.427 10 8 10s3.022-.289 4.096-.777c.324-.147.633-.323.904-.525Zm0 3V13c0 .374-.356.875-1.318 1.313C10.766 14.729 9.464 15 8 15s-2.766-.27-3.682-.687C3.356 13.875 3 13.373 3 13v-1.302c.271.202.58.378.904.525C4.978 12.71 6.427 13 8 13s3.022-.289 4.096-.777c.324-.147.633-.323.904-.525Z"/>
    </svg>
  );
}

function IconRobot(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5ZM3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.58 26.58 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a.93.93 0 0 1-.765.935c-.845.147-2.34.346-4.235.346s-3.39-.2-4.235-.346A.93.93 0 0 1 3 9.219V8.062Zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a25 25 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.02.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25 25 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.135Z"/>
      <path d="M8.5 1.866a1 1 0 1 0-1 0V3h-2A4.5 4.5 0 0 0 1 7.5V8a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v1.5A2.5 2.5 0 0 0 3.5 16h9a2.5 2.5 0 0 0 2.5-2.5V12a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1v-.5A4.5 4.5 0 0 0 10.5 3h-2V1.866ZM14 7.5V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5v-6A3.5 3.5 0 0 1 5.5 4h5A3.5 3.5 0 0 1 14 7.5Z"/>
    </svg>
  );
}

function IconChecklist(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M9.5 0a.5.5 0 0 1 .5.5.5.5 0 0 0 .5.5.5.5 0 0 1 .5.5V2a.5.5 0 0 1-.5.5h-5A.5.5 0 0 1 5 2v-.5a.5.5 0 0 1 .5-.5.5.5 0 0 0 .5-.5.5.5 0 0 1 .5-.5h3z" />
      <path d="M3 2.5a.5.5 0 0 1 .5-.5H4a.5.5 0 0 1 0 1v9a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 1 0-1h.5a.5.5 0 0 1 .5.5v10a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 12.5v-10z" />
      <path d="M10 7a.5.5 0 0 1-.146.354l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L6.5 9.293l2.646-2.647A.5.5 0 0 1 10 7z" />
    </svg>
  );
}

function IconSpeedometer(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z" />
      <path
        fillRule="evenodd"
        d="M0 10a8 8 0 1 1 15.547 2.661c-.442 1.253-1.845 1.602-2.932 1.25C11.309 13.488 9.475 13 8 13c-1.474 0-3.31.488-4.615.911-1.087.352-2.49.003-2.932-1.25A7.988 7.988 0 0 1 0 10zm8-7a7 7 0 0 0-6.603 9.329c.203.575.923.876 1.68.63C4.397 12.533 6.358 12 8 12s3.604.532 4.923.96c.757.245 1.477-.056 1.68-.631A7 7 0 0 0 8 3z"
      />
    </svg>
  );
}

const CARDS: readonly ConsoleCardConfig[] = [
  {
    key: 'tenants',
    to: '/admin/tenants',
    icon: <IconBuilding />,
    accent: '#0d6efd',
    status: 'available',
  },
  {
    key: 'skills',
    to: '/admin/skills',
    icon: <IconCpu />,
    accent: '#6f42c1',
    status: 'available',
  },
  {
    key: 'cutover',
    to: '/admin/conformita/gate',
    icon: <IconShield />,
    accent: '#dc3545',
    status: 'available',
  },
  {
    key: 'corpus',
    to: '/admin/corpus/ingestione',
    icon: <IconBook />,
    accent: '#198754',
    status: 'available',
  },
  {
    key: 'banca_dati',
    to: '/admin/corpus',
    icon: <IconDatabase />,
    accent: '#1a237e',
    status: 'available',
  },
  {
    key: 'tipi_documento',
    to: '/admin/tipi-documento',
    icon: <IconFileTextLarge />,
    accent: '#fd7e14',
    status: 'available',
  },
  {
    key: 'llm_providers',
    to: '/admin/llm-providers',
    icon: <IconRobot />,
    accent: '#20c997',
    status: 'available',
  },
  {
    key: 'compliance',
    to: '/admin/conformita',
    icon: <IconChecklist />,
    accent: '#dc3545',
    status: 'roadmap_clickable',
  },
  {
    key: 'piattaforma',
    to: '/admin/piattaforma',
    icon: <IconSpeedometer />,
    accent: '#0dcaf0',
    status: 'available',
  },
];

interface InfoTooltipProps {
  readonly title: string;
  readonly tooltipTitle: string;
  readonly body: string;
  readonly features: readonly string[];
  readonly testId: string;
}

function InfoTooltip(props: InfoTooltipProps): JSX.Element {
  const { t } = useTranslation();
  const tooltipId = useId();
  const [open, setOpen] = useState(false);

  const show = (): void => setOpen(true);
  const hide = (): void => setOpen(false);
  const toggle = (): void => setOpen((v) => !v);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className="btn btn-link p-0 border-0 text-secondary"
        style={{
          width: 22,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          lineHeight: 1,
          background: 'transparent',
        }}
        aria-label={t('admin.console.info_aria_label', { title: props.title })}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={toggle}
        data-testid={props.testId}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16Zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
        </svg>
      </button>
      {open && (
        <div
          id={tooltipId}
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 1050,
            width: 320,
            maxWidth: '90vw',
            backgroundColor: '#212529',
            color: '#fff',
            borderRadius: 6,
            padding: '0.75rem 0.9rem',
            lineHeight: 1.45,
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.28)',
            textAlign: 'left',
            whiteSpace: 'normal',
          }}
        >
          <p
            className="fw-semibold mb-1"
            style={{ color: '#fff', fontSize: '0.9rem', lineHeight: 1.25 }}
          >
            {props.tooltipTitle}
          </p>
          <p
            className="mb-2"
            style={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '0.7rem',
              lineHeight: 1.4,
            }}
          >
            {props.body}
          </p>
          {props.features.length > 0 && (
            <ul
              className="ps-3 mb-0"
              style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.78)' }}
            >
              {props.features.map((feat) => (
                <li key={feat} className="lh-sm">
                  {feat}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

function ConsoleCard(props: { readonly card: ConsoleCardConfig }): JSX.Element {
  const { t } = useTranslation();
  const { card } = props;
  const title = t(`admin.console.cards.${card.key}.title`);
  const tooltipTitle = t(`admin.console.cards.${card.key}.tooltip_title`, {
    defaultValue: title,
  });
  const body = t(`admin.console.cards.${card.key}.body`);
  const featuresKey = `admin.console.cards.${card.key}.features`;
  const features = t(featuresKey, { returnObjects: true, defaultValue: [] }) as
    | readonly string[]
    | string;
  const featureList: readonly string[] = Array.isArray(features) ? features : [];
  const isAvailable = card.status === 'available';
  const isClickableRoadmap = card.status === 'roadmap_clickable';
  const isClickable = isAvailable || isClickableRoadmap;

  return (
    <article
      className="card border-0 urania-admin-console__card h-100"
      data-testid={`admin-console-card-${card.key}`}
      style={{
        opacity: isClickable ? 1 : 0.92,
        borderLeft: `6px solid ${card.accent}`,
        borderTop: '1px solid rgba(0,0,0,0.06)',
        borderRight: '1px solid rgba(0,0,0,0.06)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <div className="card-body py-3 px-3 d-flex align-items-center gap-3">
        <div
          style={{
            color: card.accent,
            backgroundColor: `${card.accent}1f`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 12,
            flex: '0 0 auto',
          }}
          aria-hidden="true"
        >
          {card.icon}
        </div>
        <div
          className="d-flex align-items-center gap-2 flex-grow-1"
          style={{ minWidth: 0 }}
        >
          <InfoTooltip
            title={title}
            tooltipTitle={tooltipTitle}
            body={body}
            features={featureList}
            testId={`admin-console-card-${card.key}-info`}
          />
          <h2
            className="fw-semibold mb-0 text-truncate"
            title={title}
            style={{ fontSize: '1.05rem', color: '#1a2336' }}
          >
            {title}
          </h2>
        </div>
        {isAvailable ? (
          <span
            className="badge"
            style={{
              backgroundColor: '#198754',
              color: '#fff',
              fontSize: '0.62rem',
              padding: '0.4em 0.6em',
              flex: '0 0 auto',
            }}
          >
            {t('admin.console.badge.available')}
          </span>
        ) : (
          <span
            className="badge"
            style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffeaa7',
              fontSize: '0.62rem',
              padding: '0.4em 0.6em',
              flex: '0 0 auto',
            }}
          >
            {t('admin.console.badge.roadmap')}
          </span>
        )}
        <div style={{ flex: '0 0 auto' }}>
          {isClickable && card.to !== null ? (
            <Link
              to={card.to}
              className={`btn ${isAvailable ? 'btn-primary' : 'btn-outline-primary'}`}
              data-testid={`admin-console-card-${card.key}-link`}
              style={{
                fontSize: '0.85rem',
                padding: '0.35rem 0.9rem',
              }}
            >
              {isAvailable ? `${t('admin.console.cta_open')} →` : `${t('admin.console.cta_preview')} →`}
            </Link>
          ) : (
            <span className="small text-muted fst-italic">
              {t('admin.console.cta_roadmap')}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

export function AdminConsolePage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isAdmin = principalIsAnyAdmin(principal);

  if (!isAuthenticated) {
    return (
      <section data-testid="admin-console-unauthenticated">
        <h1 className="h3 mb-3">{t('admin.console.title')}</h1>
        <div className="alert alert-info" role="region">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section data-testid="admin-console-forbidden">
        <h1 className="h3 mb-3">{t('admin.console.title')}</h1>
        <div className="alert alert-warning" role="alert">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-console" data-testid="admin-console-page">
      <style>
        {`
          .urania-admin-console__card {
            box-shadow:
              0 2px 6px rgba(0, 0, 0, 0.06),
              0 4px 14px rgba(0, 0, 0, 0.04);
            transition: transform 120ms ease, box-shadow 120ms ease;
          }
          .urania-admin-console__card:hover {
            transform: translateY(-2px);
            box-shadow:
              0 6px 16px rgba(0, 0, 0, 0.12),
              0 10px 24px rgba(0, 0, 0, 0.08);
          }
        `}
      </style>
      <nav className="mb-3">
        <Link to="/dashboard" className="small text-decoration-none">
          ← {t('dashboard.title')}
        </Link>
      </nav>
      <div
        className="rounded mb-3 px-3 px-md-4 py-2 py-md-3 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: 760 }}>
          <span
            className="badge mb-1"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.65rem',
              letterSpacing: '0.05em',
            }}
          >
            ADMIN · CONSOLE
          </span>
          <h1 className="h5 mb-1">{t('admin.console.title')}</h1>
          <p
            className="mb-0 opacity-90 small"
            style={{ fontSize: '0.78rem' }}
          >
            {t('admin.console.subtitle')}
          </p>
        </div>
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-30px', right: '-30px', opacity: 0.1 }}
          width="180"
          height="180"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h.5A1.5 1.5 0 0 1 15 2.5v12a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 14.5v-12A1.5 1.5 0 0 1 2.5 1H3V.5a.5.5 0 0 1 .5-.5zM1 4v10.5A1.5 1.5 0 0 0 2.5 16h11a1.5 1.5 0 0 0 1.5-1.5V4H1z" />
        </svg>
      </div>
      <div className="row g-3">
        {CARDS.map((card) => (
          <div key={card.key} className="col-12 col-lg-6">
            <ConsoleCard card={card} />
          </div>
        ))}
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => AmministrazioneSegmentRoute,
  path: '/admin',
  component: AdminConsolePage,
});
